import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import cx from 'classnames'
import prettyBytes from 'pretty-bytes'

import Alert from 'common/components/alert/Alert'
import Button from 'common/components/button'
import Loader from 'common/components/loaders/Loader'
import { CloseSmall, ImageIcon } from 'common/icons'

// Kept under content-api's 9MB JSON body limit: base64 inflates raw bytes by
// ~4/3, so 6MB of source image data becomes ~8MB of base64 text.
const MAX_IMAGE_SIZE_BYTES = 6291456 // 6MB

export type ImageSelection = {
  file: File
  previewUrl: string | null
  dataUri: string | null
  isProcessing: boolean
  error: string | null
}

const readFileAsDataUri = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

export const buildImageSelection = async (
  file: File
): Promise<ImageSelection> => {
  const previewUrl = URL.createObjectURL(file)

  try {
    const dataUri = await readFileAsDataUri(file)
    return { file, previewUrl, dataUri, isProcessing: false, error: null }
  } catch {
    return {
      file,
      previewUrl,
      dataUri: null,
      isProcessing: false,
      error: 'Could not read this image. Please try a different file.',
    }
  }
}

type Props = {
  selection: ImageSelection | null
  onFileSelected: (file: File) => void
  onRemoveFile: () => void
}

const ImageImportTabContent = ({
  selection,
  onFileSelected,
  onRemoveFile,
}: Props) => {
  const [rejectionError, setRejectionError] = useState<string | null>(null)
  const isBusy = !!selection?.isProcessing

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    multiple: false,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    noClick: isBusy,
    noDrag: isBusy,
    noKeyboard: isBusy,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setRejectionError(
          fileRejections[0].errors[0]?.message ??
            'This file could not be accepted.'
        )
        return
      }
      setRejectionError(null)
      const file = acceptedFiles[0]
      if (file) {
        onFileSelected(file)
      }
    },
  })

  const displayError = rejectionError ?? selection?.error ?? null

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {displayError && (
        <Alert type="error" showIcon className="mb-4 shrink-0">
          {displayError}
        </Alert>
      )}
      <div
        {...getRootProps()}
        data-testid="image-upload-dropzone"
        className={cx(
          'flex-1 min-h-0 flex flex-col items-center justify-center gap-4 rounded-sm px-6 py-8 outline-none transition-colors bg-gold-300/25',
          {
            'cursor-pointer': !isBusy,
            'cursor-not-allowed': isBusy,
          }
        )}
      >
        <input {...getInputProps()} />
        {selection ? (
          <>
            {selection.isProcessing ? (
              <Loader size="small" />
            ) : selection.previewUrl ? (
              <div className="flex-1 min-h-0 w-full flex items-center justify-center">
                <img
                  src={selection.previewUrl}
                  alt="Image preview"
                  className="h-full w-full object-contain rounded-md border-2 border-gold-400 shadow-md bg-white p-2"
                  data-testid="image-thumbnail"
                />
              </div>
            ) : (
              <ImageIcon
                size={12}
                isDefaultColor={false}
                className="text-coolGray-400"
              />
            )}
            <div className="flex items-center gap-2 text-base-700 shrink-0 bg-white rounded-full border border-gold-400 shadow-sm px-4 py-2">
              <span
                className="text-sm font-medium"
                data-testid="image-file-name"
              >
                {selection.file.name} ({prettyBytes(selection.file.size)})
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveFile()
                }}
                data-testid="image-file-remove-button"
              >
                <CloseSmall size={3} isDefaultColor={false} />
              </button>
            </div>
          </>
        ) : (
          <>
            <ImageIcon
              size={12}
              isDefaultColor={false}
              className="text-coolGray-400"
            />
            <div className="text-center">
              <p className="text-2xl font-relais text-base-700">
                {isDragActive
                  ? 'Drop your file here'
                  : 'Drag & drop your file here'}
              </p>
              <p className="text-sm text-base-500 pt-2">
                Supports image files up to 6MB
              </p>
            </div>
            <Button
              variant="secondary"
              size="medium"
              data-testid="image-browse-button"
            >
              Browse files
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default ImageImportTabContent
